import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import {
    CellBadge,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination
} from 'src/registry/components/table';

import usePagination from 'src/registry/hooks/usePagination';
import useSorting from 'src/registry/hooks/useSorting';
import useSearch from 'src/registry/hooks/useSearch';
import { useListIdentifiers } from 'src/features/identifiers/api/useListIdentifiers';
import { Identifier } from 'src/features/identifiers/utils/types';
import { commonMessages } from 'src/utils/messages';
import { identifiersMessages } from 'src/features/identifiers/utils/messages';
import { identifierValidityBadgeOptions } from 'src/features/identifiers/utils/constants';
import { CellBoolean } from 'src/registry/components/table/cells/CellBoolean';
import CellDate from 'src/registry/components/table/cells/CellDate';
import { useParams } from 'react-router-dom';
import { identifierTypeBadgeOptions } from 'src/features/membership/utils/constants';
import { membershipMessages } from 'src/features/membership/utils/messages';

type IdentifiersTableProps = {
    entityId?: string;
}

const columnHelper = createColumnHelper<Identifier>();

const IdentifiersTable = ({ entityId }: IdentifiersTableProps) => {
    const { formatMessage } = useIntl();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'name', desc: false }],
    });

    const { parkingId } = useParams();

    const {
        data: listIdentifiers,
        isError,
        isLoading,
    } = useListIdentifiers({
        parkingId: parkingId ?? '',
        ...(entityId && { entity: entityId }),
        ...queryPaginationOptions,
        ...querySortingOptions,
        ...querySearchOptions,
        config: { enabled: !!parkingId}
    });

    const columns = [
        columnHelper.accessor('value', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(identifiersMessages.value),
            },
        }),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            cell: CellBadge,
            enableHiding: true,
            meta: {
                title: formatMessage(commonMessages.type),
                badgeOptions: identifierTypeBadgeOptions
            },
        }),
        columnHelper.accessor('is_used', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBoolean,
            meta: {
                title: formatMessage(identifiersMessages.isUsed),
            },
        }),
        columnHelper.accessor('validity', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(identifiersMessages.validity),
                badgeOptions: identifierValidityBadgeOptions
            },
        }),
        columnHelper.accessor('valid_from', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellDate,
            meta: {
                title: formatMessage(membershipMessages.validFrom),
            },
        }),
        columnHelper.accessor('valid_to', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellDate,
            meta: {
                title: formatMessage(membershipMessages.validTo),
            },
        }),
    ];

    return (
            <DataTable
                columns={columns as any}
                data={listIdentifiers}
                pagination={pagination}
                sorting={sorting}
                search={search}
                semantics={{
                    title: formatMessage(identifiersMessages.identifiers),
                    entryName: formatMessage(identifiersMessages.identifier).toLowerCase(),
                    entryNames: formatMessage(identifiersMessages.identifiers).toLowerCase(),
                    placeholder: formatMessage(identifiersMessages.identifier).toLowerCase(),
                }}
                isError={isError}
                isLoading={isLoading}
                getRowId={(row) => row.id.toString()}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
            />
    );
};

export default IdentifiersTable;
